import React, { useState } from 'react';
import axios from 'axios';

// Move components OUTSIDE main function
const RequestView = ({ requestForm, setRequestForm, handleSendConsent, loading, error, success }) => (
  <div className="p-6 bg-white rounded-lg shadow-lg">
    <h2 className="text-2xl font-bold mb-4">📤 Request भेजो</h2>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Patient ID</label>
        <input
          type="text"
          value={requestForm.patient_id}
          onChange={(e) => setRequestForm(f => ({...f, patient_id: e.target.value}))}
          placeholder="patient_12345"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Purpose</label>
        <select
          value={requestForm.purpose}
          onChange={(e) => setRequestForm(f => ({...f, purpose: e.target.value}))}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="care_management">Care Management</option>
          <option value="second_opinion">Second Opinion</option>
        </select>
      </div>
      <button
        onClick={handleSendConsent}
        disabled={loading}
        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
      >
        {loading ? 'Sending...' : '📤 Send Consent'}
      </button>
      {success && <p className="p-3 bg-green-50 border border-green-200 rounded text-green-800">{success}</p>}
      {error && <p className="p-3 bg-red-50 border border-red-200 rounded text-red-800">{error}</p>}
    </div>
  </div>
);

const VerifyView = ({ otpForm, setOtpForm, handleVerifyOTP, loading, error, success }) => (
  <div className="p-6 bg-white rounded-lg shadow-lg">
    <h2 className="text-2xl font-bold mb-4">✅ OTP से Verify करो</h2>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Consent ID</label>
        <input
          type="text"
          value={otpForm.consent_id}
          onChange={(e) => setOtpForm(f => ({...f, consent_id: e.target.value}))}
          placeholder="consent_12345"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">OTP</label>
        <input
          type="text"
          value={otpForm.otp}
          onChange={(e) => setOtpForm(f => ({...f, otp: e.target.value}))}
          placeholder="123456"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
        />
      </div>
      <button
        onClick={handleVerifyOTP}
        disabled={loading}
        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
      >
        {loading ? 'Verifying...' : '✅ Verify OTP'}
      </button>
      {success && <p className="p-3 bg-green-50 border border-green-200 rounded text-green-800">{success}</p>}
      {error && <p className="p-3 bg-red-50 border border-red-200 rounded text-red-800">{error}</p>}
    </div>
  </div>
);

const ListConsentView = ({ consents, loading, error }) => (
  <div className="p-6 bg-white rounded-lg shadow-lg">
    <h2 className="text-2xl font-bold mb-4">📋 Active Consents</h2>
    {loading && <p>Loading...</p>}
    {error && <p className="text-red-600">{error}</p>}
    {consents.length === 0 && <p className="text-gray-600">No active consents</p>}
    <div className="space-y-2">
      {consents.map(c => (
        <div key={c.id} className="p-4 border border-gray-200 rounded">
          <p className="font-medium">Consent #{c.id}</p>
          <p className="text-sm text-gray-600">Purpose: {c.purpose}</p>
        </div>
      ))}
    </div>
  </div>
);

const RevokeView = ({ revokeConsentId, setRevokeConsentId, handleRevokeConsent, loading, error, success }) => (
  <div className="p-6 bg-white rounded-lg shadow-lg">
    <h2 className="text-2xl font-bold mb-4">❌ Revoke Consent</h2>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Consent ID</label>
        <input
          type="text"
          value={revokeConsentId}
          onChange={(e) => setRevokeConsentId(e.target.value)}
          placeholder="consent_12345"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
        />
      </div>
      <button
        onClick={handleRevokeConsent}
        disabled={loading}
        className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
      >
        {loading ? 'Revoking...' : '❌ Revoke'}
      </button>
      {success && <p className="p-3 bg-green-50 border border-green-200 rounded text-green-800">{success}</p>}
      {error && <p className="p-3 bg-red-50 border border-red-200 rounded text-red-800">{error}</p>}
    </div>
  </div>
);

// MAIN COMPONENT
export default function ABDMConsentManager() {
  const [mode, setMode] = useState('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [requestForm, setRequestForm] = useState({
    patient_id: '',
    purpose: 'care_management',
    document_types: ['discharge_summary', 'opd_notes'],
    expiry_days: 90
  });

  const [otpForm, setOtpForm] = useState({
    consent_id: '',
    otp: ''
  });

  const [consents, setConsents] = useState([]);
  const [revokeConsentId, setRevokeConsentId] = useState('');

  const handleSendConsent = async () => {
    if (!requestForm.patient_id.trim()) {
      setError('Enter Patient ID!');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const response = await axios.post(`/api/abdm/consent/request`, requestForm);
      setSuccess('✅ Consent request sent!');
      setRequestForm(f => ({...f, patient_id: ''}));
    } catch (err) {
      setError('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpForm.consent_id.trim() || !otpForm.otp.trim()) {
      setError('Enter Consent ID and OTP!');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await axios.post(`/api/abdm/consent/verify`, otpForm);
      setSuccess('✅ Consent verified!');
      setOtpForm({consent_id: '', otp: ''});
    } catch (err) {
      setError('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeConsent = async () => {
    if (!revokeConsentId.trim()) {
      setError('Enter Consent ID!');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await axios.post(`/api/abdm/consent/revoke`, { consent_id: revokeConsentId });
      setSuccess('✅ Consent revoked!');
      setRevokeConsentId('');
    } catch (err) {
      setError('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setMode('request')} className={`px-4 py-2 rounded ${mode === 'request' ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>📤 Request</button>
        <button onClick={() => setMode('verify')} className={`px-4 py-2 rounded ${mode === 'verify' ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>✅ Verify</button>
        <button onClick={() => setMode('list')} className={`px-4 py-2 rounded ${mode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>📋 List</button>
        <button onClick={() => setMode('revoke')} className={`px-4 py-2 rounded ${mode === 'revoke' ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>❌ Revoke</button>
      </div>
      
      <div className={mode === 'request' ? 'block' : 'hidden'}>
        <RequestView requestForm={requestForm} setRequestForm={setRequestForm} handleSendConsent={handleSendConsent} loading={loading} error={error} success={success} />
      </div>
      
      <div className={mode === 'verify' ? 'block' : 'hidden'}>
        <VerifyView otpForm={otpForm} setOtpForm={setOtpForm} handleVerifyOTP={handleVerifyOTP} loading={loading} error={error} success={success} />
      </div>
      
      <div className={mode === 'list' ? 'block' : 'hidden'}>
        <ListConsentView consents={consents} loading={loading} error={error} />
      </div>
      
      <div className={mode === 'revoke' ? 'block' : 'hidden'}>
        <RevokeView revokeConsentId={revokeConsentId} setRevokeConsentId={setRevokeConsentId} handleRevokeConsent={handleRevokeConsent} loading={loading} error={error} success={success} />
      </div>
    </div>
  );
}
